'use client';
import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, BookOpen, ExternalLink, Ticket, Share2,
  Download, Eye, Calendar, MapPin, X, Loader2, AlertCircle
} from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

// ── Types ────────────────────────────────────────────────────────────────────

interface Program {
  id: string;
  title: string;
  season: string;
  program_status: string;
  views: number;
  created_at: string;
  user_id: string;
}

interface UserProfile {
  full_name: string;
  school_venue: string;
}

interface ProgramSection {
  section_id: string;
  section_data: Record<string, any>;
  is_complete: boolean;
  updated_at: string;
}

interface PageDef {
  id: string;
  label: string;
  sectionId: string;
  content: React.ReactNode;
}

// ── Section renderers ────────────────────────────────────────────────────────

function renderCover(data: Record<string, any>, title: string, season: string, schoolVenue: string) {
  const showTitle = data?.title || title || 'Untitled';
  const showSeason = data?.season || season || '';
  const venue = data?.venue || schoolVenue || '';
  const city = data?.city || '';
  const state = data?.state || '';
  const location = [city, state].filter(Boolean).join(', ');

  return (
    <div className="bg-white w-full h-full flex flex-col font-sans program-page">
      <div className="bg-zinc-950 h-16 flex items-center justify-center gap-2">
        <BookOpen size={16} className="text-yellow-400" />
        <span className="text-yellow-400 text-xs font-bold tracking-[0.25em] uppercase">Ovation Programs</span>
      </div>
      <div className="flex-1 bg-gradient-to-b from-zinc-100 to-white flex flex-col items-center justify-center px-8">
        {venue && <p className="text-[11px] tracking-widest uppercase text-zinc-500 mb-1 font-sans">{venue}</p>}
        {location && <p className="text-[10px] text-zinc-400 mb-6 font-sans">{location}</p>}
        <div className="w-full h-44 bg-gradient-to-br from-zinc-800 to-zinc-600 rounded-lg mb-6 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="text-4xl font-bold font-sans italic mb-1">{showTitle}</div>
            {showSeason && <div className="text-xs text-zinc-300 tracking-wider uppercase">{showSeason}</div>}
          </div>
        </div>
        {showSeason && <p className="text-[11px] tracking-widest uppercase text-zinc-600 font-sans">{showSeason}</p>}
      </div>
    </div>
  );
}

function renderBilling(data: Record<string, any>, title: string, schoolVenue: string) {
  const showTitle = data?.title || title || 'Untitled';
  const venue = data?.venue || schoolVenue || '';
  const city = data?.city || '';
  const state = data?.state || '';
  const location = [city, state].filter(Boolean).join(', ');
  const director = data?.director || '';
  const musicDirector = data?.musicDirector || '';
  const composer = data?.composer || '';
  const lyricist = data?.lyricist || '';
  const bookWriter = data?.bookWriter || '';
  const licensor = data?.licensor || '';

  return (
    <div className="bg-white w-full h-full flex flex-col items-center justify-center px-8 py-10 program-page">
      <div className="text-center space-y-3 max-w-xs">
        {venue && <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-sans">{venue}</p>}
        {location && <p className="text-[10px] text-zinc-400 font-sans">{location}</p>}
        <p className="text-[11px] tracking-[0.25em] uppercase text-zinc-600 font-sans pt-2">Proudly Presents</p>
        <h1 className="text-3xl font-bold text-zinc-900 font-sans">{showTitle}</h1>
        {(composer || lyricist || bookWriter) && (
          <p className="text-[10px] text-zinc-500 font-sans leading-relaxed">
            {composer && <>Music by {composer}<br /></>}
            {lyricist && <>Lyrics by {lyricist}<br /></>}
            {bookWriter && <>Book by {bookWriter}</>}
          </p>
        )}
        {licensor && (
          <p className="text-[9px] text-zinc-400 font-sans leading-relaxed border-t border-zinc-200 pt-3">{licensor}</p>
        )}
        {(director || musicDirector) && (
          <div className="border-t border-zinc-200 pt-3 space-y-1">
            {director && (
              <>
                <p className="text-[9px] text-zinc-400 font-sans">Directed by</p>
                <p className="text-[12px] font-bold text-zinc-800 font-sans">{director}</p>
              </>
            )}
            {musicDirector && (
              <>
                <p className="text-[9px] text-zinc-400 font-sans mt-2">Music Direction by</p>
                <p className="text-[12px] font-bold text-zinc-800 font-sans">{musicDirector}</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function renderDirectorNotes(data: Record<string, any>) {
  const noteText = data?.note || data?.notes || data?.content || '';
  const directorName = data?.directorName || data?.director || '';
  const directorTitle = data?.directorTitle || '';

  return (
    <div className="bg-white w-full h-full px-8 py-10 program-page">
      <h2 className="text-base font-bold text-zinc-900 font-sans border-b border-zinc-200 pb-3 mb-4">A Note from the Director</h2>
      {noteText ? (
        <p className="text-[10px] text-zinc-600 leading-relaxed font-sans whitespace-pre-wrap">{noteText}</p>
      ) : (
        <p className="text-[10px] text-zinc-400 italic font-sans text-center">No director&apos;s note added yet.</p>
      )}
      {directorName && (
        <div className="mt-6">
          <p className="text-[10px] font-bold text-zinc-800 font-sans italic">{directorName}</p>
          {directorTitle && <p className="text-[9px] text-zinc-500 font-sans">{directorTitle}</p>}
        </div>
      )}
    </div>
  );
}

function renderSongs(data: Record<string, any>) {
  const actOne: any[] = data?.actOne || data?.act1 || [];
  const actTwo: any[] = data?.actTwo || data?.act2 || [];
  const allSongs: any[] = data?.songs || [];

  const displayActOne = actOne.length > 0 ? actOne : (allSongs.length > 0 ? allSongs : []);
  const displayActTwo = actTwo.length > 0 ? actTwo : [];

  return (
    <div className="bg-white w-full h-full px-7 py-8 program-page overflow-auto">
      <h2 className="text-sm font-bold text-zinc-900 font-sans text-center mb-1">Musical Numbers</h2>
      <div className="w-12 h-px bg-zinc-300 mx-auto mb-4" />
      {displayActOne.length > 0 && (
        <>
          <p className="text-[9px] font-bold tracking-widest uppercase text-zinc-500 font-sans mb-2">
            {displayActTwo.length > 0 ? 'Act One' : 'Songs'}
          </p>
          <div className="space-y-1.5 mb-4">
            {displayActOne.map((row: any, i: number) => (
              <div key={`song-act1-${i}`} className="flex justify-between items-start gap-3">
                <span className="text-[10px] italic text-zinc-700 font-sans">{row?.title || row?.song || row?.name || ''}</span>
                <span className="text-[9px] text-zinc-400 font-sans text-right shrink-0">{row?.performers || row?.characters || row?.char || ''}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {displayActTwo.length > 0 && (
        <>
          <p className="text-[9px] font-bold tracking-widest uppercase text-zinc-500 font-sans mb-2">Act Two</p>
          <div className="space-y-1.5">
            {displayActTwo.map((row: any, i: number) => (
              <div key={`song-act2-${i}`} className="flex justify-between items-start gap-3">
                <span className="text-[10px] italic text-zinc-700 font-sans">{row?.title || row?.song || row?.name || ''}</span>
                <span className="text-[9px] text-zinc-400 font-sans text-right shrink-0">{row?.performers || row?.characters || row?.char || ''}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {displayActOne.length === 0 && displayActTwo.length === 0 && (
        <p className="text-[10px] text-zinc-400 italic font-sans text-center">No songs added yet.</p>
      )}
    </div>
  );
}

function renderCast(data: Record<string, any>) {
  const castList: any[] = data?.cast || data?.castList || data?.members || [];

  return (
    <div className="bg-white w-full h-full px-7 py-8 program-page">
      <h2 className="text-sm font-bold text-zinc-900 font-sans text-center mb-1">Cast of Characters</h2>
      <div className="w-12 h-px bg-zinc-300 mx-auto mb-4" />
      {castList.length > 0 ? (
        <div className="space-y-2">
          {castList.map((row: any, i: number) => (
            <div key={`cast-${i}`} className="flex justify-between items-center border-b border-zinc-100 pb-1.5">
              <span className="text-[10px] italic text-zinc-700 font-sans">{row?.character || row?.role || row?.char || ''}</span>
              <span className="text-[10px] text-zinc-500 font-sans">{row?.actor || row?.performer || row?.name || ''}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-zinc-400 italic font-sans text-center">No cast added yet.</p>
      )}
    </div>
  );
}

function renderCrew(data: Record<string, any>) {
  const crewList: any[] = data?.crew || data?.crewList || data?.members || [];

  return (
    <div className="bg-white w-full h-full px-7 py-8 program-page">
      <h2 className="text-sm font-bold text-zinc-900 font-sans text-center mb-1">Production Crew</h2>
      <div className="w-12 h-px bg-zinc-300 mx-auto mb-4" />
      {crewList.length > 0 ? (
        <div className="space-y-2">
          {crewList.map((row: any, i: number) => (
            <div key={`crew-${i}`} className="flex justify-between items-center border-b border-zinc-100 pb-1.5">
              <span className="text-[10px] text-zinc-500 font-sans">{row?.role || row?.position || row?.title || ''}</span>
              <span className="text-[10px] font-medium text-zinc-700 font-sans">{row?.name || row?.person || ''}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-zinc-400 italic font-sans text-center">No crew added yet.</p>
      )}
    </div>
  );
}

function renderCastBios(data: Record<string, any>) {
  const bios: any[] = data?.bios || data?.castBios || data?.members || [];

  return (
    <div className="bg-white w-full h-full px-6 py-7 program-page overflow-auto">
      <h2 className="text-sm font-bold text-zinc-900 font-sans text-center mb-4">Cast Bios</h2>
      {bios.length > 0 ? (
        <div className="space-y-4">
          {bios.map((bio: any, i: number) => (
            <div key={`bio-${i}`} className="flex gap-3">
              <div className="w-12 h-14 bg-zinc-200 rounded shrink-0 flex items-center justify-center">
                <span className="text-[9px] text-zinc-400 font-sans">Photo</span>
              </div>
              <div>
                <p className="text-[11px] font-bold text-zinc-900 font-sans">{bio?.name || ''}</p>
                <p className="text-[9px] italic text-zinc-500 font-sans mb-1">{bio?.role || bio?.character || ''}</p>
                <p className="text-[9px] text-zinc-600 font-sans leading-relaxed">{bio?.bio || bio?.description || ''}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-zinc-400 italic font-sans text-center">No cast bios added yet.</p>
      )}
    </div>
  );
}

function renderCrewBios(data: Record<string, any>) {
  const bios: any[] = data?.bios || data?.crewBios || data?.members || [];

  return (
    <div className="bg-white w-full h-full px-6 py-7 program-page overflow-auto">
      <h2 className="text-sm font-bold text-zinc-900 font-sans text-center mb-4">Crew Bios</h2>
      {bios.length > 0 ? (
        <div className="space-y-4">
          {bios.map((bio: any, i: number) => (
            <div key={`crewbio-${i}`} className="flex gap-3">
              <div className="w-12 h-14 bg-zinc-200 rounded shrink-0 flex items-center justify-center">
                <span className="text-[9px] text-zinc-400 font-sans">Photo</span>
              </div>
              <div>
                <p className="text-[11px] font-bold text-zinc-900 font-sans">{bio?.name || ''}</p>
                <p className="text-[9px] italic text-zinc-500 font-sans mb-1">{bio?.role || bio?.position || ''}</p>
                <p className="text-[9px] text-zinc-600 font-sans leading-relaxed">{bio?.bio || bio?.description || ''}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-zinc-400 italic font-sans text-center">No crew bios added yet.</p>
      )}
    </div>
  );
}

function renderPhotos(data: Record<string, any>) {
  const photos: any[] = data?.photos || data?.images || [];

  return (
    <div className="bg-white w-full h-full px-6 py-7 program-page overflow-auto">
      <h2 className="text-sm font-bold text-zinc-900 font-sans text-center mb-4">Photo Gallery</h2>
      {photos.length > 0 ? (
        <div className="grid grid-cols-2 gap-2">
          {photos.map((photo: any, i: number) => (
            <div key={`photo-${i}`} className="aspect-square bg-zinc-200 rounded overflow-hidden flex items-center justify-center">
              {photo?.url ? (
                <img src={photo.url} alt={photo?.caption || `Photo ${i + 1}`} className="w-full h-full object-cover" />
              ) : (
                <span className="text-[9px] text-zinc-400 font-sans text-center px-2">{photo?.caption || 'Photo'}</span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-zinc-400 italic font-sans text-center">No photos added yet.</p>
      )}
    </div>
  );
}

function renderAds(data: Record<string, any>) {
  const ads: any[] = data?.ads || data?.advertisements || [];
  const firstAd = ads[0];

  return (
    <div className="bg-zinc-900 w-full h-full flex flex-col items-center justify-center program-page">
      <div className="w-full h-full bg-gradient-to-br from-violet-900 via-zinc-900 to-zinc-900 flex flex-col items-center justify-center p-8">
        <div className="text-violet-400 text-xs font-bold tracking-widest uppercase mb-4 font-sans">Advertisement</div>
        {firstAd ? (
          <>
            <div className="text-white text-2xl font-bold mb-2 text-center">{firstAd?.businessName || firstAd?.name || ''}</div>
            {firstAd?.tagline && <div className="text-zinc-400 text-sm text-center mb-4">{firstAd.tagline}</div>}
            {firstAd?.website && (
              <div className="border border-violet-500/40 rounded-lg px-6 py-2 text-violet-400 text-xs font-semibold">{firstAd.website}</div>
            )}
          </>
        ) : (
          <p className="text-zinc-500 text-sm font-sans text-center">No ads added yet.</p>
        )}
      </div>
    </div>
  );
}

function renderThanks(data: Record<string, any>) {
  const thanksText = data?.thanks || data?.acknowledgements || data?.content || '';
  const thanksList: any[] = data?.list || data?.people || [];

  return (
    <div className="bg-white w-full h-full px-8 py-10 program-page overflow-auto">
      <h2 className="text-sm font-bold text-zinc-900 font-sans text-center mb-4">Special Thanks</h2>
      {thanksText ? (
        <p className="text-[10px] text-zinc-600 leading-relaxed font-sans whitespace-pre-wrap">{thanksText}</p>
      ) : thanksList.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {thanksList.map((person: any, i: number) => (
            <span key={`thanks-${i}`} className="text-[10px] text-zinc-600 font-sans">
              {typeof person === 'string' ? person : person?.name || ''}{i < thanksList.length - 1 ? ',' : ''}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-zinc-400 italic font-sans text-center">No acknowledgements added yet.</p>
      )}
    </div>
  );
}

function renderOther(data: Record<string, any>) {
  let content = data?.content || data?.text || '';
  const title = data?.title || 'Additional Information';

  return (
    <div className="bg-white w-full h-full px-8 py-10 program-page overflow-auto">
      <h2 className="text-sm font-bold text-zinc-900 font-sans border-b border-zinc-200 pb-3 mb-4">{title}</h2>
      {content ? (
        <p className="text-[10px] text-zinc-600 leading-relaxed font-sans whitespace-pre-wrap">{content}</p>
      ) : (
        <p className="text-[10px] text-zinc-400 italic font-sans text-center">No additional content added yet.</p>
      )}
    </div>
  );
}

function renderBackCover(data: Record<string, any>, title: string, season: string, schoolVenue: string) {
  const showTitle = data?.title || title || 'Untitled';
  const showSeason = data?.season || season || '';
  const venue = data?.venue || schoolVenue || '';
  const nextShow = data?.nextShow || '';
  const message = data?.message || 'Thank you for supporting live theatre in our community.';

  return (
    <div className="bg-zinc-900 w-full h-full flex flex-col items-center justify-center program-page">
      <div className="w-full h-full bg-gradient-to-b from-zinc-800 to-zinc-950 flex flex-col items-center justify-center p-8">
        <BookOpen size={24} className="text-yellow-400 mb-4" />
        <div className="text-white text-xl font-bold font-sans text-center mb-2">{showTitle}</div>
        {(venue || showSeason) && (
          <div className="text-zinc-400 text-xs text-center mb-6">
            {[venue, showSeason].filter(Boolean).join(' · ')}
          </div>
        )}
        <div className="text-zinc-500 text-[10px] text-center font-sans">
          {message}
          {nextShow && <><br />Next: {nextShow}</>}
        </div>
        <div className="mt-6 text-[9px] text-zinc-700 font-sans text-center">
          Program created with Ovation Programs<br />ovationprograms.com
        </div>
      </div>
    </div>
  );
}

// ── Build pages from section data ────────────────────────────────────────────

function buildPages(
  sectionsMap: Record<string, Record<string, any>>,
  program: Program,
  profile: UserProfile | null
): PageDef[] {
  const title = program?.title || 'Untitled';
  const season = program?.season || '';
  const schoolVenue = profile?.school_venue || '';

  const sectionOrder = [
    'cover', 'billing', 'director-notes', 'songs', 'cast', 'crew',
    'cast-bios', 'crew-bios', 'photos', 'ads', 'thanks', 'other', 'back-cover'
  ];

  const labelMap: Record<string, string> = {
    'cover': 'Cover',
    'billing': 'Billing',
    'director-notes': "Director's Note",
    'songs': 'Songs',
    'cast': 'Cast',
    'crew': 'Crew',
    'cast-bios': 'Cast Bios',
    'crew-bios': 'Crew Bios',
    'photos': 'Photos',
    'ads': 'Ads',
    'thanks': 'Thanks',
    'other': 'Other',
    'back-cover': 'Back Cover',
  };

  return sectionOrder.map((sectionId) => {
    const data = sectionsMap[sectionId] || {};
    let content: React.ReactNode;

    switch (sectionId) {
      case 'cover':
        content = renderCover(data, title, season, schoolVenue);
        break;
      case 'billing':
        content = renderBilling(data, title, schoolVenue);
        break;
      case 'director-notes':
        content = renderDirectorNotes(data);
        break;
      case 'songs':
        content = renderSongs(data);
        break;
      case 'cast':
        content = renderCast(data);
        break;
      case 'crew':
        content = renderCrew(data);
        break;
      case 'cast-bios':
        content = renderCastBios(data);
        break;
      case 'crew-bios':
        content = renderCrewBios(data);
        break;
      case 'photos':
        content = renderPhotos(data);
        break;
      case 'ads':
        content = renderAds(data);
        break;
      case 'thanks':
        content = renderThanks(data);
        break;
      case 'other':
        content = renderOther(data);
        break;
      case 'back-cover':
        content = renderBackCover(data, title, season, schoolVenue);
        break;
      default:
        content = <div className="bg-white w-full h-full flex items-center justify-center"><p className="text-zinc-400 text-xs">Section not available</p></div>;
    }

    return {
      id: `page-${sectionId}`,
      label: labelMap[sectionId] || sectionId,
      sectionId,
      content,
    };
  });
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ProgramViewerClient() {
  const searchParams = useSearchParams();
  const programId = searchParams?.get('programId') ?? null;
  const supabase = createClient();

  const [currentPage, setCurrentPage] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [program, setProgram] = useState<Program | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [sectionsMap, setSectionsMap] = useState<Record<string, Record<string, any>>>({});
  const [programPages, setProgramPages] = useState<PageDef[]>([]);

  // ── Load program + profile + sections ──────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!programId) {
      setError('No program ID provided. Please open this page from a valid program link.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Load program
      const { data: prog, error: progErr } = await supabase
        .from('programs')
        .select('id, user_id, title, season, program_status, views, created_at')
        .eq('id', programId)
        .single();

      if (progErr || !prog) {
        setError('Program not found or is not available.');
        setLoading(false);
        return;
      }

      setProgram(prog);

      // Load user profile for school/venue info
      const { data: prof } = await supabase
        .from('user_profiles')
        .select('full_name, school_venue')
        .eq('id', prog.user_id)
        .maybeSingle();

      setProfile(prof ?? null);

      // Load all sections
      const { data: sections, error: sectErr } = await supabase
        .from('program_sections')
        .select('section_id, section_data, is_complete, updated_at')
        .eq('program_id', programId);

      if (sectErr) {
        console.log('Sections load error:', sectErr.message);
      }

      const map: Record<string, Record<string, any>> = {};
      sections?.forEach((s) => {
        map[s.section_id] = s.section_data || {};
      });

      setSectionsMap(map);
    } catch (err: any) {
      console.log('Load error:', err?.message);
      setError('Failed to load program. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Rebuild pages when data changes ────────────────────────────────────────
  useEffect(() => {
    if (program) {
      setProgramPages(buildPages(sectionsMap, program, profile));
    }
  }, [program, profile, sectionsMap]);

  // ── Real-time subscription on program_sections ─────────────────────────────
  useEffect(() => {
    if (!programId) return;

    const channel = supabase
      .channel(`program_sections_viewer_${programId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'program_sections',
          filter: `program_id=eq.${programId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const updated = payload.new as ProgramSection;
            setSectionsMap((prev) => ({
              ...prev,
              [updated.section_id]: updated.section_data || {},
            }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [programId]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const goNext = () => setCurrentPage((p) => Math.min(p + 1, programPages?.length - 1));
  const goPrev = () => setCurrentPage((p) => Math.max(p - 1, 0));

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard?.writeText(url);
  };

  // ── Derived sidebar data ────────────────────────────────────────────────────
  const coverData = sectionsMap['cover'] || {};
  const billingData = sectionsMap['billing'] || {};

  const displayTitle = program?.title || 'Untitled';
  const displayVenue = coverData?.venue || profile?.school_venue || '';
  const displayCity = coverData?.city || '';
  const displayState = coverData?.state || '';
  const displayLocation = [displayCity, displayState].filter(Boolean).join(', ');
  const displaySeason = program?.season || coverData?.season || '';
  const openingDate = coverData?.openingDate || coverData?.openingNight || '';
  const closingDate = coverData?.closingDate || coverData?.closingNight || '';
  const dateRange = openingDate && closingDate
    ? `${openingDate} – ${closingDate}`
    : openingDate || closingDate || displaySeason;
  const ticketUrl = coverData?.ticketUrl || billingData?.ticketUrl || '#';
  const websiteUrl = coverData?.websiteUrl || billingData?.websiteUrl || '#';

  const page = programPages?.[currentPage];
  const prevPage = currentPage > 0 ? programPages?.[currentPage - 1] : null;

  // ── Loading state ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-16 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={32} className="text-yellow-400 animate-spin" />
          <p className="text-zinc-400 text-sm">Loading program…</p>
        </div>
      </div>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────────
  if (error || !program) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-16 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 max-w-sm text-center px-6">
          <AlertCircle size={32} className="text-red-400" />
          <p className="text-zinc-300 text-sm">{error || 'Program not found.'}</p>
          <Link
            href="/user-dashboard"
            className="text-yellow-400 text-sm hover:text-yellow-300 underline underline-offset-2"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // ── No pages yet ────────────────────────────────────────────────────────────
  if (programPages.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-16 flex items-center justify-center">
        <Loader2 size={24} className="text-yellow-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 flex relative overflow-hidden" style={{ background: '#13162a' }}>

      {/* ── Ambient gradient blobs ── */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
        <div
          className="absolute rounded-full opacity-20 blur-3xl animate-blob-1"
          style={{ width: 520, height: 520, top: '-80px', left: '-120px', background: 'radial-gradient(circle, #7c3aed, transparent 70%)' }}
        />
        <div
          className="absolute rounded-full opacity-15 blur-3xl animate-blob-2"
          style={{ width: 420, height: 420, top: '30%', right: '-100px', background: 'radial-gradient(circle, #ec4899, transparent 70%)' }}
        />
        <div
          className="absolute rounded-full opacity-10 blur-3xl animate-blob-3"
          style={{ width: 360, height: 360, bottom: '10%', left: '30%', background: 'radial-gradient(circle, #f59e0b, transparent 70%)' }}
        />
        <div
          className="absolute rounded-full opacity-15 blur-3xl animate-blob-4"
          style={{ width: 300, height: 300, bottom: '-60px', right: '25%', background: 'radial-gradient(circle, #0ea5e9, transparent 70%)' }}
        />
      </div>

      {/* Main viewer */}
      <div className="flex-1 flex flex-col items-center justify-start py-8 px-4 relative z-10">
        {/* Header bar */}
        <div
          className="w-full max-w-4xl flex items-center justify-between mb-6 px-5 py-4 rounded-xl border"
          style={{ background: '#1e2235', borderColor: 'rgba(255,255,255,0.07)' }}
        >
          {/* Colorful accent bar on left edge */}
          <div className="flex items-center gap-4">
            <div className="w-1 h-10 rounded-full shrink-0" style={{ background: 'linear-gradient(to bottom, #7c3aed, #ec4899, #f59e0b)' }} />
            <div>
              <h1 className="text-lg font-bold text-zinc-100 font-sans">{displayTitle}</h1>
              <p className="text-xs text-zinc-500">
                {[displayVenue, displayLocation, displaySeason].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs text-zinc-500">
              <Eye size={13} />
              <span className="tabular-nums">{program?.views?.toLocaleString() ?? 0}</span>
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 border text-zinc-300 text-xs font-semibold px-3 py-2 rounded-lg transition-all duration-150 active:scale-95"
              style={{ background: '#252a40', borderColor: 'rgba(255,255,255,0.1)' }}
            >
              <Share2 size={13} />
              Share
            </button>
            <Link
              href="/sign-up-login-screen"
              className="flex items-center gap-1.5 text-zinc-950 text-xs font-bold px-3 py-2 rounded-lg transition-all duration-150 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}
            >
              <Download size={13} />
              Get PDF — $50
            </Link>
          </div>
        </div>

        {/* Flipbook viewer */}
        <div className="w-full max-w-4xl flex items-center justify-center gap-6">
          {/* Prev button */}
          <button
            onClick={goPrev}
            disabled={currentPage === 0}
            className="w-10 h-10 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 shrink-0 border"
            style={{ background: '#1e2235', borderColor: 'rgba(255,255,255,0.1)' }}
          >
            <ChevronLeft size={20} />
          </button>

          {/* Two-page spread on desktop */}
          <div className="hidden lg:flex gap-0 items-stretch drop-shadow-2xl">
            {/* Left page (previous or blank) */}
            <div
              className="w-56 xl:w-64 2xl:w-72 aspect-[5.5/8.5] rounded-l-lg overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.08)', boxShadow: '-4px 0 24px rgba(0,0,0,0.5)' }}
            >
              {prevPage ? (
                <div className="w-full h-full">{prevPage?.content}</div>
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ background: '#1e2235' }}>
                  <span className="text-zinc-600 text-xs font-sans">Cover</span>
                </div>
              )}
            </div>
            {/* Spine */}
            <div className="w-1.5 self-stretch" style={{ background: 'linear-gradient(to bottom, #7c3aed, #ec4899, #f59e0b, #0ea5e9)' }} />
            {/* Right page (current) */}
            <div
              className="w-56 xl:w-64 2xl:w-72 aspect-[5.5/8.5] rounded-r-lg overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.08)', boxShadow: '4px 0 24px rgba(0,0,0,0.5)' }}
            >
              <div className="w-full h-full">{page?.content}</div>
            </div>
          </div>

          {/* Single page on mobile */}
          <div className="lg:hidden w-full max-w-xs">
            <div
              className="w-full aspect-[5.5/8.5] rounded-xl overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
            >
              <div className="w-full h-full">{page?.content}</div>
            </div>
          </div>

          {/* Next button */}
          <button
            onClick={goNext}
            disabled={currentPage === programPages?.length - 1}
            className="w-10 h-10 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 shrink-0 border"
            style={{ background: '#1e2235', borderColor: 'rgba(255,255,255,0.1)' }}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Page indicator */}
        <div className="flex items-center gap-3 mt-6">
          <span className="text-xs text-zinc-500 tabular-nums">
            Page {currentPage + 1} of {programPages?.length}
          </span>
          <div className="flex gap-1">
            {programPages?.map((p, i) => (
              <button
                key={p?.id}
                onClick={() => setCurrentPage(i)}
                className={`h-1.5 rounded-full transition-all duration-150 ${
                  i === currentPage ? 'w-4' : 'w-1.5 hover:bg-zinc-500'
                }`}
                style={i === currentPage ? { background: 'linear-gradient(90deg, #7c3aed, #ec4899)' } : { background: '#3f4460' }}
              />
            ))}
          </div>
        </div>

        {/* Section thumbnails */}
        <div className="w-full max-w-4xl mt-8 overflow-x-auto">
          <div className="flex gap-2 pb-2">
            {programPages?.map((p, i) => {
              const accentColors = ['#7c3aed','#ec4899','#f59e0b','#0ea5e9','#10b981','#f97316','#8b5cf6','#06b6d4','#84cc16','#e11d48','#a855f7','#14b8a6','#fbbf24'];
              const accent = accentColors[i % accentColors.length];
              return (
                <button
                  key={p?.id}
                  onClick={() => setCurrentPage(i)}
                  className="shrink-0 flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-150 border"
                  style={i === currentPage
                    ? { background: '#1e2235', borderColor: accent }
                    : { background: 'transparent', borderColor: 'transparent' }
                  }
                >
                  <div
                    className="w-10 h-14 rounded overflow-hidden flex flex-col items-center justify-start"
                    style={{ background: '#252a40', border: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    <div className="w-full h-1" style={{ background: accent }} />
                    <div className="flex-1 flex items-center justify-center px-1">
                      <span className="text-[7px] text-zinc-500 text-center leading-tight">{p?.label}</span>
                    </div>
                  </div>
                  <span className="text-[9px] text-zinc-500 w-12 text-center leading-tight truncate">{p?.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div
        className={`${sidebarOpen ? 'w-72' : 'w-0'} shrink-0 transition-all duration-300 overflow-hidden relative z-10`}
        style={{ borderLeft: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="w-72 h-full flex flex-col pt-24 p-5" style={{ background: '#1a1d2e' }}>
          {/* Colorful accent bar at top of sidebar */}
          <div className="w-full h-1 rounded-full mb-5" style={{ background: 'linear-gradient(90deg, #7c3aed, #ec4899, #f59e0b, #0ea5e9)' }} />

          <div className="flex items-center justify-between mb-5">
            <h2 className="font-bold text-zinc-100 text-sm">Show Info</h2>
            <button onClick={() => setSidebarOpen(false)} className="text-zinc-500 hover:text-zinc-300">
              <X size={16} />
            </button>
          </div>

          <div className="space-y-4 mb-6">
            <div
              className="rounded-lg p-3"
              style={{ background: '#252a40', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Production</div>
              <div className="font-bold text-zinc-100 font-sans">{displayTitle}</div>
            </div>
            {displayVenue && (
              <div
                className="rounded-lg p-3"
                style={{ background: '#252a40', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Venue</div>
                <div className="text-sm text-zinc-300">{displayVenue}</div>
              </div>
            )}
            {displayLocation && (
              <div className="flex items-center gap-2 px-1">
                <MapPin size={13} className="text-violet-400 shrink-0" />
                <span className="text-sm text-zinc-400">{displayLocation}</span>
              </div>
            )}
            {dateRange && (
              <div className="flex items-center gap-2 px-1">
                <Calendar size={13} className="text-sky-400 shrink-0" />
                <span className="text-sm text-zinc-400">{dateRange}</span>
              </div>
            )}
            {program?.program_status && (
              <div>
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Status</div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  program.program_status === 'published' ?'bg-green-900/40 text-green-400' :'bg-zinc-800 text-zinc-400'
                }`}>
                  {program.program_status.charAt(0).toUpperCase() + program.program_status.slice(1)}
                </span>
              </div>
            )}
          </div>

          <div className="space-y-2 mb-6">
            <a
              href={ticketUrl}
              className="flex items-center gap-2 w-full font-bold text-sm px-4 py-2.5 rounded-xl transition-all duration-150 active:scale-95 justify-center text-zinc-950"
              style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}
            >
              <Ticket size={15} />
              Buy Tickets
            </a>
            <a
              href={websiteUrl}
              className="flex items-center gap-2 w-full text-zinc-300 text-sm font-semibold px-4 py-2.5 rounded-xl transition-all duration-150 active:scale-95 justify-center border"
              style={{ background: '#252a40', borderColor: 'rgba(255,255,255,0.08)' }}
            >
              <ExternalLink size={15} />
              Theatre Website
            </a>
          </div>

          {profile?.full_name && (
            <div
              className="rounded-lg p-3"
              style={{ background: '#252a40', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">Director</div>
              <p className="text-sm text-zinc-300 font-sans">{profile.full_name}</p>
            </div>
          )}
        </div>
      </div>

      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed right-4 top-24 text-zinc-400 hover:text-zinc-100 p-2 rounded-lg transition-all duration-150 z-20 border"
          style={{ background: '#1e2235', borderColor: 'rgba(255,255,255,0.1)' }}
        >
          <BookOpen size={16} />
        </button>
      )}
    </div>
  );
}